import React, { useEffect } from 'react';

import Layout from '../components/layout';
import SEO from '../components/seo';
import { ScrollToPlugin } from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { Statistic } from '../statistics/statistic';
import { StatisticPanel } from '../statistics/statisticPanel';
import gsap from 'gsap';
import headerImage from '../images/banner/banner-3.jpg';
import { isIE } from '../helpers/isIE';

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

const KPIs = () => {
    useEffect(() => {
        if (!isIE && window.innerWidth > 768) {
            //Panel intro animation - late addition. Ideally the panels should be a component but too much refactoring required
            const panels = document.querySelectorAll('.stat-panel, .stat-grid-image > div');
            for (let i = 0; i < panels.length; i++) {
                const element = panels[i];
                gsap.set(element.parentNode, { perspective: 800 });
                gsap.from(element, {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top bottom',
                        // start: 'top bottom-=150px',
                        end: '+=50',
                        scrub: 1.2,
                        once: true,
                    },
                    opacity: 0,
                    rotationX: 90,
                    ease: 'back.out(1.5)',
                });
            }
        }
    }, []);

    return (
        <Layout
            headerTitle="Key Performance Indicators"
            headerIntro="To keep track of how we’re performing, we have indicators which are reported to Government. More detailed information is provided in the Key Performance Indicators section of our Annual Report."
            headerImage={headerImage}
        >
            <SEO title="Key Performance Indicators" />
            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="20%"
                                label="of community members recognise their local natural hazard risks and are prepared to take action."
                                title="Hazard"
                                description="20% of community members recognise their local natural hazard risks and are prepared to take action. This means they not only recognise they are at risk of bushfire, flood, storm or cyclone but have also either prepared an emergency kit or written an emergency plan."
                                className="stat--dark-blue"
                                tableData={['20%', '15%', '+5%']}
                            />
                            <Statistic
                                value="72.33%"
                                isDouble
                                label="of scheduled operational courses are delivered."
                                title="Operational Courses"
                                description="Our result was lower than target due to restrictions caused by COVID-19 where face to face scheduled training courses were cancelled from 17 March 2020 till 15 June 2020. With the social distancing restrictions now lifted, we expect to meet the target in the future as face to face training courses have recommenced."
                                className="stat--grey stat--margin"
                                tableData={['72.33%', '80%', '-7.67%']}
                            />
                        </div>
                        <Statistic
                            isLarge
                            value="$11.75"
                            label="was the average cost to deliver education and awareness programs and emergency hazard information, 36 cents per household more than our target of $11.39."
                            title="Cost to deliver awareness programs"
                            description="Research shows informed communities are better able to manage their risks, reducing the impact of natural hazards and other preventable emergencies. That is why we invest in a range of education and awareness programs able to reach WA’s diverse communities."
                            className="stat--gold"
                            tableData={['$11.75', '$11.39', '+$0.36']}
                        />
                    </div>
                </div>
            </div>

            <div className="o-section o-section--no-space-xs">
                <div className="stat-panel">
                    <div className="grid-container">
                        <div className="row">
                            <StatisticPanel
                                value="$9.02"
                                label="was the average cost to deliver health, safety, wellbeing and training services which was lower than our target of $9.65."
                                title="Cost to deliver training services"
                                description={
                                    <>
                                        <p>
                                            It is essential our people are trained and supported to maintain their physical and mental
                                            health while providing safe and efficient services to the community.
                                        </p>
                                        <p>
                                            Our cost to deliver health, safety, wellbeing and training services was lower than our target of
                                            $9.65 due to restrictions caused by COVID-19 where face to face scheduled training courses were
                                            cancelled from 17 March 2020 till 15 June 2020.
                                        </p>
                                    </>
                                }
                                className="stat--dark-blue"
                                tableData={['$9.02', ' $9.65', '-$0.63 ']}
                            />
                            <div className="stat-panel__image">
                                <div style={{ backgroundImage: `url('/annualreport2020/images/stats/training-wellbeing.jpg')` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <Statistic
                            isLarge
                            value="100%"
                            label="of Level 3 incident Impact Statements are completed and endorsed by the State Recovery Coordinator."
                            title="Impact statements completed"
                            description="Impact Statements allow effective engagement and planning with local government trying to recover from a major incident. In partnering with them, we can ensure the right recovery support is provided to the community."
                            className="stat--grey"
                            tableData={['100%', '80%', '+20%']}
                        />
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="87.86%"
                                label="of structure fire reports were completed within specified timeframes."
                                title="Structure fire reports completed within timeframes"
                                description={
                                    <>
                                        <p>
                                            When incident reports are completed promptly, it enables buildings to be repaired / rebuilt more
                                            quickly, aiding recovery and reducing the impact on families and businesses.
                                        </p>
                                        <p>
                                            Our result of 87.86% of structure fire reports being completed promptly is below target. This
                                            can be attributed to the structure fire incidents referred to our Fire Investigation Analysis
                                            Unit for investigation of the cause of the fire. These investigations can be protracted and
                                            delay the completion of the structure fire incident report. The importance of completing
                                            incident reports within the specified timeframes will continue to be emphasised to all
                                            responding crews.
                                        </p>
                                    </>
                                }
                                className="stat--dark-blue"
                                tableData={['87.86%', '90%', '-2.14%']}
                            />
                            <Statistic
                                value="20%"
                                isDouble
                                label="of natural hazard events caused a significant impact on the community."
                                title="Natural hazard events"
                                description="The result of 20% was lower than our target of 25%. This shows our preparedness before a natural hazard event is making a difference."
                                tableData={['20%', '25%', '+5%']}
                                className="stat--gold stat--margin"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="o-section o-section--no-space-xs">
                <div className="stat-panel">
                    <div className="grid-container">
                        <div className="row">
                            <div className="stat-panel__image stat-panel__image--flipped">
                                <div style={{ backgroundImage: `url('/annualreport2020/images/stats/ses-cliff-top.jpg')` }}></div>
                            </div>
                            <StatisticPanel
                                isFlipped
                                value="94.19%"
                                label="of responses to ESL 1 and ESL 2 incidents met target times."
                                title="ESL incident target times"
                                description="94.19% of responses to ESL 1 and ESL 2 incidents met target times."
                                tableData={['94.19%', '90%', '+4.19%']}
                                className="stat--dark-blue"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="73.27%"
                                label="of structure fires were confined."
                                title="Structure fires confined"
                                description="This reflects that through effective community fire safety education programs and our response, 980 homes that had a fire were able to be saved this year."
                                tableData={['73.27%', '72%', '+1.26%']}
                                className="stat--light"
                            />
                        </div>
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                value="$163.20"
                                isDouble
                                label="was the average cost to deliver frontline services which was higher than our target of $154.85."
                                title="Average cost for frontline services"
                                description={
                                    <div>
                                        <p style={{ marginTop: '1.5em' }}>
                                            <strong>Frontline (or community facing) services include:</strong>
                                        </p>
                                        <ul className="list-centered">
                                            <li>incident responders</li>
                                            <li>fit-for-purpose response appliances, equipment and facilities</li>
                                            <li>advice to the community and industry</li>
                                            <li>management or mitigation of risks to prevent incidents from occurring</li>
                                        </ul>
                                        <p>
                                            Our result of $163.20 per person to deliver frontline services was higher than our target of
                                            $154.85. This can be contributed to the drying climate, changing emergency management landscape
                                            and the overlap of fire seasons in the north and south of the State.
                                        </p>
                                    </div>
                                }
                                tableData={['$163.20', '$154.85', '+$8.35']}
                                className="stat--gold"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default KPIs;
